import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Spin, Result } from "antd";
import DataProvider from "../../services";
import { useAppStore } from "../../store";
import { resetProfileCompletionGate } from "../../lib/profile-completion";
import {
  trackOAuthEvent,
  trackLogin,
  trackSignup,
} from "../../utils/analytics";

export default function OAuthSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, setUser } = useAppStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleOAuthSuccess = async () => {
      try {
        const token = searchParams.get("token");
        const refreshToken = searchParams.get("refreshToken");

        if (!token) {
          setError("No token received from authentication provider");
          setLoading(false);
          return;
        }

        // Store tokens
        await DataProvider.setToken(token);
        if (refreshToken) {
          await DataProvider.setRefreshToken(refreshToken);
        }
        setToken({ token });
        // New login - the "complete your profile" nudge should be eligible
        // to show again, regardless of when it last appeared.
        resetProfileCompletionGate();

        // Get user profile
        const { data } = await DataProvider.getProfile();
        const userWith = { ...data, timenow: new Date() };
        setUser(userWith);

        // Determine if this is a signup or login based on URL params
        const isSignup = searchParams.get("signup") === "true";
        const provider =
          (searchParams.get("provider") as "google" | "apple") || "google";

        if (isSignup) {
          trackSignup(provider, true);
        } else {
          trackLogin(provider, true);
        }
        trackOAuthEvent(provider, isSignup ? "signup" : "login", true);

        setLoading(false);

        // Redirect to main page after a short delay
        setTimeout(() => {
          navigate("/main");
        }, 1500);
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to complete authentication",
        );
        setLoading(false);
      }
    };

    handleOAuthSuccess();
  }, [searchParams, navigate, setToken, setUser]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-gray-600">Completing authentication...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Result
          status="error"
          title="Authentication Failed"
          subTitle={error}
          extra={[
            <button
              key="retry"
              onClick={() => navigate("/sign-in")}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Login
            </button>,
          ]}
        />
      </div>
    );
  }

  return (
    <div className="h-screen flex items-center justify-center">
      <Result
        status="success"
        title="Authentication Successful!"
        subTitle="Redirecting you to your dashboard..."
      />
    </div>
  );
}
